import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_DOUBLES = { total_doubles: 520, wins: 385, losses: 135, win_rate: "74.04" };
const DEFAULT_SINGLES = { total_matches: 605, wins: 466, losses: 139, win_rate: "77.02" };

const CountUp = ({ value, decimals = 0, suffix = "" }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const [display, setDisplay] = useState((0).toFixed(decimals));

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, parseFloat(value) || 0, {
            duration: 1.8,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setDisplay(v.toFixed(decimals)),
        });
        return () => controls.stop();
    }, [inView, value, decimals]);

    return (
        <span ref={ref}>
            {display}
            {suffix}
        </span>
    );
};

const StatGroup = ({ label, items }) => (
    <div>
        <div className="font-mono text-[10px] tracking-[0.3em] text-bn-cyan font-semibold text-center mb-8">{label}</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10">
            {items.map((s, i) => (
                <motion.div
                    key={s.id}
                    data-testid={s.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="text-center lg:border-l border-bn-line lg:first:border-l-0"
                >
                    <div className={`font-display font-extrabold text-4xl lg:text-5xl tracking-tight ${s.color}`}>
                        <CountUp value={s.value} decimals={s.decimals || 0} suffix={s.suffix || ""} />
                    </div>
                    <div className="mt-2.5 font-mono text-[10px] tracking-[0.25em] text-slate-500">{s.label}</div>
                </motion.div>
            ))}
        </div>
    </div>
);

export const StatsBar = () => {
    const [doubles, setDoubles] = useState(DEFAULT_DOUBLES);
    const [singles, setSingles] = useState(DEFAULT_SINGLES);

    useEffect(() => {
        axios
            .get(`${API}/content`)
            .then((res) => {
                if (res.data?.stats) setDoubles(res.data.stats);
                if (res.data?.singles_stats) setSingles(res.data.singles_stats);
            })
            .catch(() => {});
    }, []);

    return (
        <section data-testid="stats-bar-section" className="relative py-14 lg:py-20 border-b border-white/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 space-y-16">
                <StatGroup
                    label="OFFER 1 — SINGLES · ODDS 1.90 – 2.50"
                    items={[
                        { id: "stats-total-matches-singles", value: singles.total_matches, label: "TOTAL MATCHES", color: "text-white" },
                        { id: "stats-wins-singles", value: singles.wins, label: "WINS", color: "text-bn-green" },
                        { id: "stats-losses-singles", value: singles.losses, label: "LOSSES", color: "text-red-400" },
                        { id: "stats-win-rate-singles", value: singles.win_rate, label: "WIN RATE", color: "text-bn-blue", decimals: 2, suffix: "%" },
                    ]}
                />
                <StatGroup
                    label="OFFER 2 — DAILY DOUBLES"
                    items={[
                        { id: "stats-total-doubles", value: doubles.total_doubles, label: "TOTAL DOUBLES", color: "text-white" },
                        { id: "stats-wins", value: doubles.wins, label: "WINS", color: "text-bn-green" },
                        { id: "stats-losses", value: doubles.losses, label: "LOSSES", color: "text-red-400" },
                        { id: "stats-win-rate", value: doubles.win_rate, label: "WIN RATE", color: "text-bn-blue", decimals: 2, suffix: "%" },
                    ]}
                />
            </div>
        </section>
    );
};
